import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSectionMessages } from "../../actions/sectionsActions";
import { resolveApiUrl } from "../../utils/apiUrls";
import {
  getSectionMediaApiRoot,
  getSectionMediaOrigin,
  absolutizeSectionMediaPath,
} from "../../utils/sectionsMediaApi";
import PresseTextOnly from "../presseView/types/PresseTextOnly";
import PresseImageOnly from "../presseView/types/PresseImageOnly";
import PresseVideoOnly from "../presseView/types/PresseVideoOnly";
import PresseImageVideo from "../presseView/types/PresseImageVideo";
import "../../styles/pages/MessagesList.scss";

const getPresseViewType = (p) => {
  const hasImage = Array.isArray(p.media) && p.media.some(m => (m.type || "").toLowerCase().includes("image"));
  const hasVideo = Array.isArray(p.media) && p.media.some(m => (m.type || "").toLowerCase().includes("video"));

  if (!hasImage && !hasVideo) return "text-only";
  if (hasImage && !hasVideo) return "image-only";
  if (!hasImage && hasVideo) return "video-only";
  if (hasImage && hasVideo) return "image-and-video";
  return "unknown";
};

export default function SectionList({
  category = 'culturel',
  sectionTitle = 'Culturel',
  emptyLabel = 'Aucun contenu culturel',
}) {
  const dispatch = useDispatch();
  const presses = useSelector((s) => s.sections.filteredMessages);
  const [localPresses, setLocalPresses] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(false);

  const BASE_URL = useMemo(
    () =>
      resolveApiUrl(
        process.env.REACT_APP_CULTUREL_BASE_URL || process.env.REACT_APP_BASE_URL,
        getSectionMediaOrigin(category),
        'BASE_URL'
      ),
    [category]
  );

  const [activeId, setActiveId] = useState(null);
  const toggle = (id) => setActiveId(prev => prev === id ? null : id);
  const isActive = (id) => activeId === id;

  const [videoActiveId, setVideoActiveId] = useState(null);
  const toggleVideo = (id) => setVideoActiveId(prev => prev === id ? null : id);
  const isVideoActive = (id) => videoActiveId === id;

  const videoRefs = useRef({});

  const pressesKey = useMemo(() => {
    if (!Array.isArray(presses)) return "";
    return presses.map((p) => p?.id).filter(Boolean).join(",");
  }, [presses]);

  useEffect(() => { dispatch(fetchSectionMessages(category)); }, [dispatch, category]);

  /** Évite d’afficher l’ancienne liste enrichie quand les ids Redux changent (sinon médias décalés / cartes obsolètes). */
  useLayoutEffect(() => {
    setLocalPresses([]);
    setIsMediaLoading(false);
  }, [pressesKey]);

  useEffect(() => {
    let cancelled = false;
    if (!Array.isArray(presses) || presses.length === 0) {
      setLocalPresses([]);
      setIsMediaLoading(false);
      return;
    }
    const valid = presses.filter((p) => p && p.id);
    if (valid.length === 0) {
      setLocalPresses([]);
      setIsMediaLoading(false);
      return;
    }

    const load = async () => {
      const mediaRoot = getSectionMediaApiRoot(category).replace(/\/$/, "");
      const token = localStorage.getItem("accessToken");
      const sanitized = valid.map((p) => {
        const { media: _ignoreReduxMedia, ...rest } = p;
        return { ...rest, media: [] };
      });

      setLocalPresses(sanitized);
      setIsMediaLoading(true);

      let cursor = 0;
      const concurrency = Math.min(6, sanitized.length);
      const workers = Array.from({ length: concurrency }, async () => {
        while (!cancelled) {
          const index = cursor++;
          if (index >= sanitized.length) return;
          const p = sanitized[index];
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 6000);
          try {
            const res = await fetch(`${mediaRoot}/getMedia/${p.id}`, {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            });
            if (!res.ok) continue;
            const data = await res.json();
            const raw = Array.isArray(data) ? data : [];
            const normalized = raw
              .filter((f) => f && String(f.messageId) === String(p.id))
              .map((f) => ({
                ...f,
                path: absolutizeSectionMediaPath(f.url || f.path, category),
              }));

            if (!cancelled) {
              setLocalPresses((prev) =>
                prev.map((item) => (item.id === p.id ? { ...item, media: normalized } : item))
              );
            }
          } catch {
            // Keep this card visible with text-only fallback if media lookup fails.
          } finally {
            clearTimeout(timeoutId);
          }
        }
      });

      await Promise.all(workers);
      if (!cancelled) setIsMediaLoading(false);
    };

    load();
    return () => {
      cancelled = true;
      setIsMediaLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- médias rechargés quand pressesKey (ids) change
  }, [pressesKey]);

  const isHydratingMedia = !Array.isArray(presses);

  const displayPresses = useMemo(() => {
    const src = localPresses.length > 0 ? localPresses : presses;
    if (!Array.isArray(src)) return [];
    return src.map((p) => ({
      ...p,
      media: Array.isArray(p.media) ? p.media : [],
    }));
  }, [localPresses, presses]);

  return (
    <div className="presse">
      <div className="presse__container">
        <div className="presse__container__title">📍 {sectionTitle}</div>

        <div className="presse__container__messagelist">
          {isHydratingMedia ? (
            <div className="presse__container__messagelist__loading" aria-label="Chargement des medias">
              <div className="presse__spinner" />
            </div>
          ) : !Array.isArray(presses) ? (
            <p className="presse__container__messagelist__error">⚠️ Erreur : données non disponibles.</p>
          ) : presses.length === 0 ? (
            <div className="presse__container__messagelist__empty">
              <h3 className="presse__container__messagelist__empty__nothing">📭 {emptyLabel}</h3>
              <p className="presse__container__messagelist__empty__add">Aucun contenu disponible.</p>
            </div>
          ) : (
            <>
              {isMediaLoading && (
                <div className="presse__container__messagelist__loading" aria-label="Chargement des medias">
                  <div className="presse__spinner" />
                </div>
              )}
              {displayPresses.map((p) => {
                const type = getPresseViewType(p);

                if (type === "text-only")
                  return <PresseTextOnly key={p.id} presse={p} isActive={isActive} toggle={toggle} />;

                if (type === "image-only")
                  return <PresseImageOnly key={p.id} presse={p} isActive={isActive} toggle={toggle} BASE_URL={BASE_URL} />;

                if (type === "video-only")
                  return (
                    <PresseVideoOnly
                      key={p.id}
                      presse={p}
                      isActive={isActive}
                      toggle={toggle}
                      isVideoActive={isVideoActive}
                      toggleVideo={toggleVideo}
                      BASE_URL={BASE_URL}
                      videoRefs={videoRefs}
                    />
                  );

                if (type === "image-and-video")
                  return (
                    <PresseImageVideo
                      key={p.id}
                      presse={p}
                      isActive={isActive}
                      toggle={toggle}
                      isVideoActive={isVideoActive}
                      toggleVideo={toggleVideo}
                      BASE_URL={BASE_URL}
                      videoRefs={videoRefs}
                    />
                  );

                return (
                  <div key={p.id} className="presse__message presse__message--unknown">
                    <p>⚠️ Format non reconnu.</p>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
